"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	UserPlus,
	Search,
	ChevronLeft,
	ChevronRight,
	Edit,
	Trash2,
	RotateCcw,
	Ban,
	Shield,
	X,
} from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { CreateUserModal } from "./create-user-modal";
import { EditUserModal } from "./edit-user-modal";
import { hasPermission } from "@/lib/permissions";
import { ResetPasswordModal } from "./reset-password-modal";
import { AuthenticatedAPIClient } from "@/lib/authenticated-api";
import { facultyLabel, useFaculties } from "@/components/faculty-select";
import { Toast } from "@/lib/utils";
import { ROLE_LABELS, ROLE_OPTIONS, roleLabel, type UserRole } from "@/lib/roles";

interface User {
	id: string;
	name: string;
	email: string;
	role?: string;
	banned?: boolean;
	banReason?: string;
	// Dates, not strings: `authClient.admin.listUsers` hands back parsed `Date`
	// objects. The old `string` here only ever compiled because the cast below
	// was loose enough to hide it.
	banExpires?: Date;
	createdAt: Date;
	updatedAt: Date;
	MaCB?: string;
	faculty_code?: string | null;
	/** `{"export_excel": true}` — khoá vắng mặt nghĩa là không có quyền. */
	permissions?: Record<string, boolean> | null;
}

const ITEMS_PER_PAGE = 10;

export default function UserManagement() {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [totalUsers, setTotalUsers] = useState(0);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

	// Chỉ để đổi mã khoa lưu trên tài khoản thành tên đọc được. Danh sách hỏng thì
	// `facultyLabel` trả về chính mã đó — vẫn đúng, chỉ kém dễ đọc.
	const { faculties } = useFaculties();

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	const loadUsers = async (page: number = 1, search: string = "") => {
		setLoading(true);
		try {
			const offset = (page - 1) * ITEMS_PER_PAGE;

			// If no search term, load all users
			if (!search.trim()) {
				const { data } = await authClient.admin.listUsers({
					query: {
						limit: ITEMS_PER_PAGE,
						offset,
						sortBy: "createdAt",
						sortDirection: "desc",
					},
				});

				if (data) {
					setUsers(data.users as User[]);
					setTotalUsers(data.total);
				}
			} else {
				// For search, we need to fetch more users and filter client-side
				// since better-auth doesn't support multi-field search
				const searchLower = search.toLowerCase().trim();

				// Fetch a larger batch to account for filtering
				const { data } = await authClient.admin.listUsers({
					query: {
						limit: Math.max(100, ITEMS_PER_PAGE * 5), // Fetch more for filtering
						offset: 0, // Always start from beginning for search
						sortBy: "createdAt",
						sortDirection: "desc",
					},
				});

				if (data) {
					// Filter users by name, email, or MaCB (case-insensitive)
					const filteredUsers = (data.users as User[]).filter(
						(user) =>
							user.name?.toLowerCase().includes(searchLower) ||
							user.email?.toLowerCase().includes(searchLower) ||
							user.MaCB?.toLowerCase().includes(searchLower),
					);

					// Implement pagination on filtered results
					const startIndex = (page - 1) * ITEMS_PER_PAGE;
					const endIndex = startIndex + ITEMS_PER_PAGE;
					const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

					setUsers(paginatedUsers);
					setTotalUsers(filteredUsers.length);
				}
			}
		} catch (error) {
			console.error("Failed to load users:", error);
			Toast.error("Không thể tải danh sách người dùng", "Vui lòng thử lại sau.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadUsers(currentPage, debouncedSearchTerm);
	}, [currentPage, debouncedSearchTerm]);

	const handleSearch = (value: string) => {
		setSearchTerm(value);
		setCurrentPage(1);
	};

	const handleDeleteUser = async (user: User) => {
		if (!confirm(`Bạn có chắc chắn muốn xóa người dùng ${user.name}?`)) {
			return;
		}

		try {
			await authClient.admin.removeUser({
				userId: user.id,
			});

			Toast.success(`Đã xóa người dùng ${user.name} thành công!`);
			loadUsers(currentPage, debouncedSearchTerm);
		} catch (error) {
			console.error("Failed to delete user:", error);
			Toast.error("Không thể xóa người dùng", "Vui lòng thử lại sau.");
		}
	};

	const handleBanUser = async (user: User) => {
		const banReason = prompt("Nhập lý do cấm tài khoản:");
		if (!banReason) return;

		try {
			await authClient.admin.banUser({
				userId: user.id,
				banReason,
			});

			Toast.success(`Đã cấm tài khoản ${user.name} thành công!`);
			loadUsers(currentPage, debouncedSearchTerm);
		} catch (error) {
			console.error("Failed to ban user:", error);
			Toast.error("Không thể cấm tài khoản", "Vui lòng thử lại sau.");
		}
	};

	const handleUnbanUser = async (user: User) => {
		try {
			await authClient.admin.unbanUser({
				userId: user.id,
			});

			Toast.success(`Đã bỏ cấm tài khoản ${user.name} thành công!`);
			loadUsers(currentPage, debouncedSearchTerm);
		} catch (error) {
			console.error("Failed to unban user:", error);
			Toast.error("Không thể bỏ cấm tài khoản", "Vui lòng thử lại sau.");
		}
	};

	const handleSetRole = async (user: User, newRole: UserRole) => {
		try {
			// Use custom backend API for role management since better-auth doesn't support manager role
			await AuthenticatedAPIClient.patch(`/users/${user.id}/role`, { role: newRole });

			// Nhãn lấy từ `ROLE_LABELS`, không map cứng: bản cũ dùng chuỗi ba nhánh và
			// mọi vai trò không phải admin/manager đều bị gọi là "người dùng" — gán
			// `viewer` sẽ báo thành công kèm đúng một câu sai.
			Toast.success(
				`Đã cập nhật quyền ${user.name} thành ${ROLE_LABELS[newRole].toLowerCase()} thành công!`,
			);
			loadUsers(currentPage, debouncedSearchTerm);
		} catch (error) {
			console.error("Failed to set role:", error);
			Toast.error("Không thể thay đổi quyền", "Vui lòng thử lại sau.");
		}
	};

	const totalPages = Math.ceil(totalUsers / ITEMS_PER_PAGE);

	const handleModalClose = () => {
		setShowCreateModal(false);
		setShowEditModal(false);
		setShowResetPasswordModal(false);
		setSelectedUser(null);
		loadUsers(currentPage, debouncedSearchTerm);
	};

	const getRoleBadgeColor = (role: string) => {
		switch (role) {
			case "admin":
				return "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300";
			case "manager":
				return "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300";
			case "user":
				return "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300";
			case "viewer":
				return "bg-muted text-muted-foreground";
			default:
				return "bg-muted text-foreground";
		}
	};

	return (
		<div className="space-y-6">
			{/* Header Actions */}
			<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
				<div className="flex-1 max-w-md">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
						<Input
							placeholder="Tìm kiếm theo tên, email hoặc mã CB..."
							value={searchTerm}
							onChange={(e) => handleSearch(e.target.value)}
							className="pl-10 pr-10"
						/>
						{searchTerm && (
							<button
								onClick={() => handleSearch("")}
								className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
								title="Xóa tìm kiếm"
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>

				<Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
					<UserPlus className="h-4 w-4" />
					Thêm người dùng
				</Button>
			</div>

			{/* User List */}
			<div className="space-y-4">
				{/* Search Status */}
				{debouncedSearchTerm && (
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-950 dark:border-blue-800">
						<p className="text-blue-800 text-sm dark:text-blue-300">
							<Search className="inline h-4 w-4 mr-1" />
							Tìm kiếm "{debouncedSearchTerm}" - {totalUsers} kết quả
						</p>
					</div>
				)}

				{loading ? (
					// Same card shape as a loaded user row, so the list does not jump on arrival.
					Array.from({ length: 4 }).map((_, i) => (
						<Card key={i}>
							<CardContent className="p-6">
								<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
									<div className="flex-1 space-y-2">
										<div className="flex items-center gap-3">
											<Skeleton className="h-6 w-48" />
											<Skeleton className="h-5 w-16 rounded-full" />
										</div>
										<Skeleton className="h-4 w-64" />
										<Skeleton className="h-4 w-40" />
									</div>
									<div className="flex gap-2">
										<Skeleton className="h-8 w-20" />
										<Skeleton className="h-8 w-20" />
									</div>
								</div>
							</CardContent>
						</Card>
					))
				) : users.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						Không tìm thấy người dùng nào
					</div>
				) : (
					users.map((user) => (
						<Card key={user.id}>
							<CardContent className="p-6">
								<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
									{/* `min-w-0`: flex item mặc định `min-width:auto` nên không co dưới
									    bề rộng nội dung — một email dài đẩy cả cột vượt mép ở 390px. */}
									<div className="min-w-0 flex-1">
										{/* `flex-wrap`: hàng tên + badge không xuống dòng được thì badge
										    cuối bị đẩy ra ngoài màn hình. */}
										<div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
											<h3 className="font-semibold text-lg">{user.name}</h3>
											<Badge className={getRoleBadgeColor(user.role || "user")}>
												{roleLabel(user.role)}
											</Badge>
											{user.banned && (
												<Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">
													Bị cấm
												</Badge>
											)}
											{/* Chỉ hiện khi đã cấp: đây là ngoại lệ được cấp riêng, nên nó
										    đáng chú ý khi có, còn "không có" mới là mặc định. */}
											{hasPermission(user.permissions, "export_excel") && (
												<Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">
													Tải Excel
												</Badge>
											)}
											{hasPermission(user.permissions, "access_dashboard") && (
												<Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300">
													Dashboard
												</Badge>
											)}
										</div>
										<div className="space-y-1 text-sm text-muted-foreground">
											<p className="break-all">Email: {user.email}</p>
											{user.MaCB && <p>Mã CB: {user.MaCB}</p>}
											{/* Hiện cả khi trống: khoa quyết định tài khoản xem được dữ liệu
											    của ai, nên "chưa gán" là thông tin cần thấy, không phải một
											    dòng để lược đi. */}
											<p>
												Khoa:{" "}
												{user.faculty_code ? (
													facultyLabel(user.faculty_code, faculties)
												) : (
													<span className="text-amber-600 dark:text-amber-400">
														chưa gán — chưa xem được dữ liệu
													</span>
												)}
											</p>
											<p>Ngày tạo: {new Date(user.createdAt).toLocaleString("vi-VN")}</p>
											{user.banned && user.banReason && (
												<p className="text-red-600 dark:text-red-400">
													Lý do cấm: {user.banReason}
												</p>
											)}
										</div>
									</div>

									<div className="flex flex-wrap gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => {
												setSelectedUser(user);
												setShowEditModal(true);
											}}
											className="flex items-center gap-1"
										>
											<Edit className="h-3 w-3" />
											Sửa
										</Button>

										<Button
											variant="outline"
											size="sm"
											onClick={() => {
												setSelectedUser(user);
												setShowResetPasswordModal(true);
											}}
											className="flex items-center gap-1"
										>
											<RotateCcw className="h-3 w-3" />
											Reset MK
										</Button>

										{/* Role Management Buttons.
										    Duyệt `ROLE_OPTIONS` thay cho chuỗi if/else theo từng vai trò: chuỗi
										    cũ có 3 nhánh cho 3 vai trò, nên vai trò thứ tư rơi vào nhánh mặc
										    định và không còn cách nào đưa về `user`. Dạng này tự đúng với mọi
										    vai trò trong danh sách, và ngắn hơn khoảng hai phần ba. */}
										{ROLE_OPTIONS.filter((value) => value !== (user.role || "user")).map(
											(value) => (
												<Button
													key={value}
													variant="outline"
													size="sm"
													onClick={() => handleSetRole(user, value)}
													className="flex items-center gap-1"
												>
													<Shield className="h-3 w-3" />→ {ROLE_LABELS[value]}
												</Button>
											),
										)}

										{user.banned ? (
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleUnbanUser(user)}
												className="flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-400"
											>
												<Ban className="h-3 w-3" />
												Bỏ cấm
											</Button>
										) : (
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleBanUser(user)}
												className="flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-400"
											>
												<Ban className="h-3 w-3" />
												Cấm
											</Button>
										)}

										<Button
											variant="outline"
											size="sm"
											onClick={() => handleDeleteUser(user)}
											className="flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-400"
										>
											<Trash2 className="h-3 w-3" />
											Xóa
										</Button>
									</div>
								</div>
							</CardContent>
						</Card>
					))
				)}
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-between">
					<p className="text-sm text-muted-foreground">
						Hiển thị {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalUsers)} -{" "}
						{Math.min(currentPage * ITEMS_PER_PAGE, totalUsers)} của {totalUsers} người dùng
					</p>

					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
							disabled={currentPage === 1}
							className="flex items-center gap-1"
						>
							<ChevronLeft className="h-4 w-4" />
							Trước
						</Button>

						<span className="px-3 py-1 text-sm bg-muted rounded">
							{currentPage} / {totalPages}
						</span>

						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
							disabled={currentPage === totalPages}
							className="flex items-center gap-1"
						>
							Sau
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
				</div>
			)}

			{/* Modals */}
			<CreateUserModal isOpen={showCreateModal} onClose={handleModalClose} />

			{selectedUser && (
				<>
					<EditUserModal isOpen={showEditModal} user={selectedUser} onClose={handleModalClose} />

					<ResetPasswordModal
						isOpen={showResetPasswordModal}
						user={selectedUser}
						onClose={handleModalClose}
					/>
				</>
			)}
		</div>
	);
}
